import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import {
  IconBrandStrava,
  IconBolt,
  IconWorld,
  IconRefresh,
  IconPalette,
  IconChevronRight,
  IconChevronDown,
  IconChevronUp,
  IconBrandDiscord,
  IconMail,
} from '@tabler/icons-react';
import stravaConnectButton from '../assets/strava/btn_strava_connect_with_orange.svg';
import Footer from '../components/Footer';
import ZwiftAhaCard from '../components/ZwiftAhaCard';
import ZwiftWorldGallery from '../components/ZwiftWorldGallery';
import Zwift3DTeaser from '../components/Zwift3DTeaser';
import MultiPlatformGallery from '../components/MultiPlatformGallery';
import { StatGuarantee } from '../components/SocialProof';
import useJsonLd from '../seo/useJsonLd';
import { ZWIFT_MAPS_ASSET } from '../utils/zwiftAssets';
import './ChallengePage.css';
import './ZwiftFixPage.css';
const marketingSeo = require('../seo/marketingSeo');

// 2D and 3D fixes are live for Zwift; Rouvy and MyWhoosh are coming soon.
const SHOW_ZWIFT_3D_TEASER = true;

// A small, asset-free before/after mock for the logged-out hero. It conveys the
// core promise (broken Solomon-Islands map → real world map) without depending on
// a hosted image, so the page is complete today. A design clip can replace it later.
const BeforeAfterMock = ({ t }) => (
  <div className="zfix-mock">
    <div className="zfix-mock-card zfix-mock-before">
      <span className="zfix-mock-tag">{t('fixPage.mockBeforeTag', 'On Strava today')}</span>
      <svg viewBox="0 0 200 150" className="zfix-mock-map zfix-mock-map-before" aria-hidden="true">
        <rect width="200" height="150" fill="#0a4d8c" />
        <rect width="200" height="150" fill="url(#oceanGrad)" />
        <defs>
          <linearGradient id="oceanGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1f74c4" />
            <stop offset="100%" stopColor="#0a3f73" />
          </linearGradient>
        </defs>
        <polyline
          points="92,70 100,62 108,72 104,84 96,82 94,74"
          fill="none"
          stroke="#fc5200"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="100" cy="74" r="22" fill="none" stroke="#ffffff" strokeOpacity="0.45" strokeDasharray="3 4" />
      </svg>
      <span className="zfix-mock-pin">📍 {t('fixPage.mockBeforePin', 'Solomon Islands?')}</span>
    </div>

    <div className="zfix-mock-arrow" aria-hidden="true">→</div>

    <div className="zfix-mock-card zfix-mock-after">
      <span className="zfix-mock-tag zfix-mock-tag-after">{t('fixPage.mockAfterTag', 'With Mythic Maps')}</span>
      <svg viewBox="0 0 200 150" className="zfix-mock-map zfix-mock-map-after" aria-hidden="true">
        <rect width="200" height="150" fill="#0484e4" />
        <path d="M0,95 Q50,80 95,92 T200,86 L200,150 L0,150 Z" fill="#3a7d2c" />
        <path d="M0,110 Q60,100 120,112 T200,104 L200,150 L0,150 Z" fill="#2f6a23" />
        <polyline
          points="40,120 70,96 95,108 120,80 150,98 172,72"
          fill="none"
          stroke="#fc5200"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="zfix-mock-pin zfix-mock-pin-after">🗺️ {t('fixPage.mockAfterPin', 'Watopia')}</span>
    </div>
  </div>
);

// Real before/after hero: a Watopia ride stranded in the ocean on Strava, then the
// same ride on the actual Watopia map (assets/zwift_maps). Falls back to the SVG
// mock if either render fails to load. Named export: the umbrella homepage reuses
// it as the Zwift fix demo section.
export const HeroVisual = ({ t }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <BeforeAfterMock t={t} />;
  }
  return (
    <div className="zfix-hero-visual">
      <figure className="zfix-hero-shot">
        <img
          src={ZWIFT_MAPS_ASSET('Watopia_portrait_1_original.png')}
          alt={t('fixPage.heroBeforeAlt', 'A Zwift ride stranded in the ocean on Strava')}
          onError={() => setFailed(true)}
        />
        <figcaption className="zfix-hero-shot-cap">{t('fixPage.mockBeforeTag', 'On Strava today')}</figcaption>
      </figure>
      <div className="zfix-mock-arrow" aria-hidden="true">→</div>
      <figure className="zfix-hero-shot">
        <img
          src={ZWIFT_MAPS_ASSET('Watopia_portrait_1_2D.png')}
          alt={t('fixPage.heroAfterAlt', 'The same ride on the real Zwift world map')}
          onError={() => setFailed(true)}
        />
        <figcaption className="zfix-hero-shot-cap zfix-hero-shot-cap-after">{t('fixPage.mockAfterTag', 'With Mythic Maps')}</figcaption>
      </figure>
    </div>
  );
};

const ZwiftFixPage = ({ user, onLoginRequired, onStravaConnect }) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const landingViewedRef = useRef(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    if (landingViewedRef.current) return;
    landingViewedRef.current = true;
    posthog?.capture('fix_landing_viewed', { is_authenticated: !!user });
  }, [posthog, user]);

  const handleConnect = () => {
    posthog?.capture('fix_connect_clicked', { is_authenticated: !!user });
    if (!user) {
      // The hero already carries the Zwift message, so go straight to Strava OAuth
      // instead of opening the (art-focused) global signup modal.
      if (onStravaConnect) {
        onStravaConnect();
      } else if (onLoginRequired) {
        onLoginRequired();
      }
      return;
    }
    // Logged-in visitors see the aha card at the top of this page; scroll to it.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const faqs = marketingSeo.FIX_FAQS.map((f) => ({ q: t(f.qKey, f.q), a: t(f.aKey, f.a) }));

  // JSON-LD + title (shared source; only injects if the static HTML lacks it).
  // This page renders only at /fix; "/" is the umbrella homepage.
  useJsonLd('/fix');

  return (
    <div className="challenge-page zwift-fix-page">
      <div className="challenge-container">

        {/* Header */}
        <div className="challenge-header-section">
          <div className="challenge-header">
            <h1>
              <IconRefresh size={32} style={{ color: '#fc5200', marginRight: 10, flexShrink: 0 }} />
              {t('fixPage.title', 'Fix your indoor cycling map on Strava')}
            </h1>
            <p className="challenge-subtitle">
              {t('fixPage.subtitle', 'Zwift puts your rides in the middle of the ocean. We swap in the real world map, automatically. Free.')}
            </p>
          </div>
        </div>

        {/* Logged-in: the personalised aha. Logged-out: the hero + connect CTA. */}
        {user ? (
          <div className="section-dark zfix-aha-section">
            <div className="section-inner">
              <div className="zfix-aha-stack">
                <ZwiftAhaCard user={user} />
              </div>
            </div>
          </div>
        ) : (
          <div className="section-dark zfix-hero-section">
            <div className="section-inner">
              <div className="zfix-hero">
                <HeroVisual t={t} />
                <div className="zfix-hero-text">
                  <h2>{t('fixPage.heroHeadline', 'Your Zwift rides, finally on the right map.')}</h2>
                  <p>{t('fixPage.heroBody', 'Connect Strava once and we fix your last indoor ride on the spot, then every future one automatically. No more Solomon Islands.')}</p>
                  <button type="button" className="zfix-strava-cta" onClick={handleConnect}>
                    <img src={stravaConnectButton} alt={t('fixPage.ctaStrava', 'Connect with Strava and fix my last ride')} />
                  </button>
                  <p className="zfix-hero-freenote">
                    {t('fixPage.heroFreeNote', 'Free. We never touch your stats. Disconnect in two clicks.')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* How it works */}
        <div className="section-darker">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('fixPage.howItWorks', 'How it works')}</h2>
              </div>
              <div className="zfix-steps">
                <div className="zfix-step">
                  <div className="zfix-step-badge">1</div>
                  <div className="zfix-step-icon"><IconBrandStrava size={22} /></div>
                  <h3>{t('fixPage.step1Title', 'Connect Strava')}</h3>
                  <p>{t('fixPage.step1Desc', 'One tap. We only read your route and update the activity photo.')}</p>
                </div>
                <div className="zfix-step">
                  <div className="zfix-step-badge">2</div>
                  <div className="zfix-step-icon"><IconWorld size={22} /></div>
                  <h3>{t('fixPage.step2Title', 'We detect indoor rides')}</h3>
                  <p>{t('fixPage.step2Desc', 'We spot Zwift activities and rebuild the actual world map from your route.')}</p>
                </div>
                <div className="zfix-step">
                  <div className="zfix-step-badge">3</div>
                  <div className="zfix-step-icon"><IconBolt size={22} /></div>
                  <h3>{t('fixPage.step3Title', 'The real map lands on Strava')}</h3>
                  <p>{t('fixPage.step3Desc', 'Automatically, on every ride. Your feed finally looks right.')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What you get */}
        <div className="section-dark">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('fixPage.whatYouGet', 'Why riders use it')}</h2>
              </div>
              <div className="zfix-features">
                <div className="zfix-feature">
                  <span className="zfix-feature-icon"><IconRefresh size={20} /></span>
                  <h3>{t('fixPage.feat1Title', 'No more Solomon Islands')}</h3>
                  <p>{t('fixPage.feat1Desc', 'Every indoor ride shows the real Zwift world it was ridden in.')}</p>
                </div>
                <div className="zfix-feature">
                  <span className="zfix-feature-icon"><IconBolt size={20} /></span>
                  <h3>{t('fixPage.feat2Title', 'Fully automatic')}</h3>
                  <p>{t('fixPage.feat2Desc', 'Turn it on once. We fix every future ride with zero clicks.')}</p>
                </div>
                <div className="zfix-feature">
                  <span className="zfix-feature-icon"><IconBrandStrava size={20} /></span>
                  <h3>{t('fixPage.feat3Title', 'Safe on your Strava')}</h3>
                  <p>{t('fixPage.feat3Desc', 'We change the photo only. Never your route, time, power, or PRs.')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust + social proof */}
        <div className="section-default zfix-proof-row">
          <div className="section-inner">
            <StatGuarantee />
          </div>
        </div>

        {/* Real before/after renders for five worlds (placeholders shown while loading) */}
        <ZwiftWorldGallery />

        {/* Platform status: Zwift live, Rouvy and MyWhoosh coming soon */}
        <MultiPlatformGallery />

        {/* Realistic 3D Zwift maps, showcased with a real Watopia render */}
        {SHOW_ZWIFT_3D_TEASER && <Zwift3DTeaser />}

        {/* Nav helper: send art-seekers to the Art section */}
        <div className="section-darker zfix-art-helper-section">
          <div className="section-inner">
            <Link
              to="/art"
              className="zfix-art-helper"
              onClick={() => posthog?.capture('fix_to_art_clicked')}
            >
              <span className="zfix-art-helper-icon"><IconPalette size={22} /></span>
              <span className="zfix-art-helper-text">
                <span className="zfix-art-helper-title">{t('fixPage.artHelperTitle', 'Here for artistic maps of your outdoor rides?')}</span>
                <span className="zfix-art-helper-sub">{t('fixPage.artHelperSub', 'Explore Mythic Maps art styles and challenges.')}</span>
              </span>
              <IconChevronRight size={20} className="zfix-art-helper-arrow" />
            </Link>
          </div>
        </div>

        {/* Final CTA (logged-out only) */}
        {!user && (
          <div className="section-inner challenge-cta-wrapper">
            <div className="challenge-cta-banner">
              <button type="button" className="zfix-strava-cta" onClick={handleConnect}>
                <img src={stravaConnectButton} alt={t('fixPage.ctaStrava', 'Connect with Strava and fix my last ride')} />
              </button>
            </div>
          </div>
        )}

        {/* FAQ */}
        <div className="section-default">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('fixPage.faqTitle', 'Frequently asked questions')}</h2>
              </div>
              <div className="faq-list">
                {faqs.map((faq, index) => (
                  <div key={index} className={`faq-item ${openFaq === index ? 'open' : ''}`}>
                    <button
                      type="button"
                      className="faq-question"
                      onClick={() => {
                        const isOpening = openFaq !== index;
                        setOpenFaq(isOpening ? index : null);
                        if (isOpening) posthog?.capture('fix_faq_toggled', { faq_index: index });
                      }}
                    >
                      <span>{faq.q}</span>
                      {openFaq === index ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
                    </button>
                    {openFaq === index && (
                      <div className="faq-answer">
                        <p>{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Community */}
        <div className="section-dark">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('fixPage.communityTitle', 'Questions or ideas?')}</h2>
              </div>
              <div className="community-cards">
                <a
                  href="https://www.strava.com/clubs/mythicmaps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="community-card strava-card"
                  onClick={() => posthog?.capture('fix_community_link_clicked', { platform: 'strava_club' })}
                >
                  <div className="community-card-icon strava-icon"><IconBrandStrava size={24} /></div>
                  <div className="community-card-text">
                    <h3>{t('fixPage.stravaClubTitle', 'Strava Club')}</h3>
                    <p>{t('fixPage.stravaClubDesc', 'Join the Mythic Maps club for other indoor riders.')}</p>
                  </div>
                  <IconChevronRight size={18} className="community-card-arrow" />
                </a>
                <a
                  href="https://discord.gg/neXEXTcQpW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="community-card discord-card"
                  onClick={() => posthog?.capture('fix_community_link_clicked', { platform: 'discord' })}
                >
                  <div className="community-card-icon discord-icon"><IconBrandDiscord size={24} /></div>
                  <div className="community-card-text">
                    <h3>{t('fixPage.discordTitle', 'Discord')}</h3>
                    <p>{t('fixPage.discordDesc', 'Request a feature or tell us which app to support next.')}</p>
                  </div>
                  <IconChevronRight size={18} className="community-card-arrow" />
                </a>
                <a
                  href="mailto:contact@mythicmaps.ai"
                  className="community-card email-card"
                  onClick={() => posthog?.capture('fix_community_link_clicked', { platform: 'email' })}
                >
                  <div className="community-card-icon email-icon"><IconMail size={24} /></div>
                  <div className="community-card-text">
                    <h3>{t('fixPage.emailTitle', 'Email us')}</h3>
                    <p>{t('fixPage.emailDesc', 'Questions, ideas, or feedback? Drop us a line.')}</p>
                  </div>
                  <IconChevronRight size={18} className="community-card-arrow" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default ZwiftFixPage;
